import { useEffect, useRef } from "react";
import { linksIntoOneNetwork } from "./neuralNetwork";

interface Node {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  colour: string;
}

interface Signal {
  from: Node;
  to: Node;
  progress: number;
  speed: number;
}

interface Spark {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  colour: string;
}

/** Where the field is drawn: on the signed-out hero's near-black stage, or behind a signed-in page in either theme. */
export type NeuralFieldTone = "stage" | "dark" | "light";

interface Palette {
  /** The nodes' and sparks' colours. */
  colours: readonly string[];
  /** A travelling signal's bright core, then the colour its glow fades through. */
  signalCore: string;
  signalGlow: string;
}

const palettes: Record<NeuralFieldTone, Palette> = {
  // Brand primaries and Sky Blue (STYLES.md), which all glow on the hero's near-black, and on the dark theme's plum.
  stage: { colours: ["#e60054", "#8353fd", "#66c9ff", "#ffffff"], signalCore: "255 255 255", signalGlow: "102 201 255" },
  dark: { colours: ["#e60054", "#8353fd", "#66c9ff", "#ffffff"], signalCore: "255 255 255", signalGlow: "102 201 255" },
  // On a light page white disappears: Galaxy Purple takes its place, and signals are Hidcote Blue.
  light: { colours: ["#e60054", "#8353fd", "#66c9ff", "#3e016f"], signalCore: "131 83 253", signalGlow: "131 83 253" },
};

const linkDistance = 130;
const pointerDistance = 180;
/** How long every node must stay in one network before the field calls it connected, so a node on the edge of range cannot trigger it. */
const connectHoldMs = 1500;
/** How long a detonation's scatter lasts before the field starts again from fresh nodes. */
const detonationMs = 900;

const pick = <T,>(items: readonly T[]): T => items[Math.floor(Math.random() * items.length)];

/**
 * A field of drifting nodes that link up when they come close, pass signals along those links, lean towards the pointer and burst
 * when clicked: a small neural network to sit behind the signed-out hero, or behind a signed-in page. Decorative only, so hidden
 * from assistive technology. It draws a single still frame when `animate` is false, stops drawing while off screen, and does
 * nothing where canvas is unavailable.
 *
 * @param props.animate - Whether the field moves; false for a still frame (reduced motion, or the viewer paused or turned it off).
 * @param props.tone - The background it is drawn on, which sets its colours.
 * @param props.interactive - Whether it leans towards the pointer and bursts on a click; false for a field that only drifts.
 * @param props.onConnected - Called once every node has stayed linked into a single network for a moment: the signed-out page's easter
 *   egg. Not called while `awake`, and only meaningful while the field is interactive and animating.
 * @param props.awake - Whether the network has woken: it stops following the pointer and holds still, its links brighten and pulse,
 *   and signals fly along them.
 * @param props.explosion - A count that blows the network apart each time it goes up: the nodes fly outward in a burst of sparks, and
 *   the field then starts again from fresh nodes, so it is no longer joined up.
 */
export function NeuralField({
  animate,
  tone = "stage",
  interactive = true,
  onConnected,
  awake = false,
  explosion = 0,
}: {
  animate: boolean;
  tone?: NeuralFieldTone;
  interactive?: boolean;
  onConnected?: () => void;
  awake?: boolean;
  explosion?: number;
}): React.JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // The field's effect must not restart (it reseeds the nodes) when these change, so it reads them from here.
  const connectedRef = useRef(onConnected);
  const awakeRef = useRef(awake);
  const detonateRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    connectedRef.current = onConnected;
    awakeRef.current = awake;
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    const host = canvas?.parentElement;
    if (!canvas || !context || !host) {
      return;
    }

    let width = 0;
    let height = 0;
    let nodes: Node[] = [];
    const signals: Signal[] = [];
    const sparks: Spark[] = [];
    const pointer = { x: 0, y: 0, active: false };
    let frame = 0;
    let visible = true;
    let connectedSince: number | null = null;
    let dragUntil = 0;
    let reseedTimer = 0;
    const { colours, signalCore, signalGlow } = palettes[tone];

    const seed = (): void => {
      const count = Math.min(110, Math.max(28, Math.round((width * height) / 9000)));
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
        radius: 1 + Math.random() * 1.8,
        colour: pick(colours),
      }));
    };

    const resize = (): void => {
      const ratio = window.devicePixelRatio || 1;
      width = host.clientWidth;
      height = host.clientHeight;
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      seed();
      draw();
    };

    const watchForConnection = (): void => {
      if (!connectedRef.current || awakeRef.current || !linksIntoOneNetwork(nodes, linkDistance)) {
        connectedSince = null;
        return;
      }

      const now = performance.now();
      connectedSince ??= now;
      if (now - connectedSince >= connectHoldMs) {
        connectedSince = null;
        connectedRef.current();
      }
    };

    const step = (): void => {
      const isAwake = awakeRef.current;
      const exploding = performance.now() < dragUntil;
      const drag = isAwake ? 0.9 : exploding ? 0.975 : 0.995;
      for (const node of nodes) {
        if (pointer.active && !isAwake && !exploding) {
          const dx = pointer.x - node.x;
          const dy = pointer.y - node.y;
          const distance = Math.hypot(dx, dy);
          if (distance < pointerDistance && distance > 1) {
            node.vx += (dx / distance) * 0.012;
            node.vy += (dy / distance) * 0.012;
          }
        }

        node.vx *= drag;
        node.vy *= drag;
        node.x += node.vx;
        node.y += node.vy;
        if (node.x < 0 || node.x > width) {
          node.vx *= -1;
          node.x = Math.min(Math.max(node.x, 0), width);
        }
        if (node.y < 0 || node.y > height) {
          node.vy *= -1;
          node.y = Math.min(Math.max(node.y, 0), height);
        }
      }

      watchForConnection();

      if (signals.length < (isAwake ? 40 : 14) && Math.random() < (isAwake ? 0.7 : 0.08)) {
        const from = pick(nodes);
        const near = nodes.filter((node) => node !== from && Math.hypot(node.x - from.x, node.y - from.y) < linkDistance);
        if (near.length > 0) {
          signals.push({ from, to: pick(near), progress: 0, speed: 0.012 + Math.random() * 0.02 });
        }
      }

      for (let i = signals.length - 1; i >= 0; i -= 1) {
        signals[i].progress += signals[i].speed;
        if (signals[i].progress >= 1) {
          signals.splice(i, 1);
        }
      }

      for (let i = sparks.length - 1; i >= 0; i -= 1) {
        const spark = sparks[i];
        spark.x += spark.vx;
        spark.y += spark.vy;
        spark.vx *= 0.96;
        spark.vy *= 0.96;
        spark.life -= 0.02;
        if (spark.life <= 0) {
          sparks.splice(i, 1);
        }
      }
    };

    const draw = (): void => {
      context.clearRect(0, 0, width, height);
      // A waking network's links brighten and throb together, as one thing.
      const awakeGlow = awakeRef.current ? 1.6 + 0.6 * Math.sin(performance.now() / 150) : 1;
      context.lineWidth = awakeRef.current ? 1.5 : 1;

      for (let i = 0; i < nodes.length; i += 1) {
        for (let j = i + 1; j < nodes.length; j += 1) {
          const a = nodes[i];
          const b = nodes[j];
          const distance = Math.hypot(a.x - b.x, a.y - b.y);
          if (distance < linkDistance) {
            context.strokeStyle = `rgb(131 83 253 / ${Math.min((1 - distance / linkDistance) * 0.45 * awakeGlow, 1).toFixed(3)})`;
            context.beginPath();
            context.moveTo(a.x, a.y);
            context.lineTo(b.x, b.y);
            context.stroke();
          }
        }

        const node = nodes[i];
        if (pointer.active && !awakeRef.current) {
          const distance = Math.hypot(pointer.x - node.x, pointer.y - node.y);
          if (distance < pointerDistance) {
            context.strokeStyle = `rgb(230 0 84 / ${((1 - distance / pointerDistance) * 0.6).toFixed(3)})`;
            context.beginPath();
            context.moveTo(pointer.x, pointer.y);
            context.lineTo(node.x, node.y);
            context.stroke();
          }
        }
      }

      for (const node of nodes) {
        context.fillStyle = node.colour;
        context.globalAlpha = 0.85;
        context.beginPath();
        context.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        context.fill();
      }

      context.globalAlpha = 1;
      for (const signal of signals) {
        const x = signal.from.x + (signal.to.x - signal.from.x) * signal.progress;
        const y = signal.from.y + (signal.to.y - signal.from.y) * signal.progress;
        const glow = context.createRadialGradient(x, y, 0, x, y, 7);
        glow.addColorStop(0, `rgb(${signalCore} / 0.95)`);
        glow.addColorStop(0.4, `rgb(${signalGlow} / 0.6)`);
        glow.addColorStop(1, `rgb(${signalGlow} / 0)`);
        context.fillStyle = glow;
        context.beginPath();
        context.arc(x, y, 7, 0, Math.PI * 2);
        context.fill();
      }

      for (const spark of sparks) {
        context.globalAlpha = Math.max(spark.life, 0);
        context.fillStyle = spark.colour;
        context.beginPath();
        context.arc(spark.x, spark.y, 2, 0, Math.PI * 2);
        context.fill();
      }
      context.globalAlpha = 1;
    };

    const loop = (): void => {
      step();
      draw();
      frame = visible ? requestAnimationFrame(loop) : 0;
    };

    const start = (): void => {
      if (animate && visible && frame === 0) {
        frame = requestAnimationFrame(loop);
      }
    };

    const localPoint = (event: PointerEvent): { x: number; y: number } => {
      const bounds = canvas.getBoundingClientRect();
      return { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
    };

    const onMove = (event: PointerEvent): void => {
      const point = localPoint(event);
      pointer.x = point.x;
      pointer.y = point.y;
      pointer.active = true;
    };

    const onLeave = (): void => {
      pointer.active = false;
    };

    const onDown = (event: PointerEvent): void => {
      const point = localPoint(event);
      for (let i = 0; i < 26; i += 1) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 1.5 + Math.random() * 3.5;
        sparks.push({ x: point.x, y: point.y, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed, life: 1, colour: pick(colours) });
      }
      for (const node of nodes) {
        const dx = node.x - point.x;
        const dy = node.y - point.y;
        const distance = Math.hypot(dx, dy);
        if (distance < pointerDistance && distance > 1) {
          node.vx += (dx / distance) * 2.2;
          node.vy += (dy / distance) * 2.2;
        }
      }
    };

    const detonate = (): void => {
      // The pointer cannot gather the scattered nodes back together, and once they have flown the field starts again from fresh ones.
      dragUntil = performance.now() + detonationMs;
      for (const node of nodes) {
        const dx = node.x - width / 2;
        const dy = node.y - height / 2;
        const distance = Math.max(Math.hypot(dx, dy), 1);
        const speed = 4 + Math.random() * 6;
        node.vx += (dx / distance) * speed;
        node.vy += (dy / distance) * speed;
        for (let i = 0; i < 4; i += 1) {
          const angle = Math.random() * Math.PI * 2;
          const burst = 1 + Math.random() * 5;
          sparks.push({ x: node.x, y: node.y, vx: Math.cos(angle) * burst, vy: Math.sin(angle) * burst, life: 1, colour: pick(colours) });
        }
      }
      signals.length = 0;
      window.clearTimeout(reseedTimer);
      reseedTimer = window.setTimeout(() => {
        dragUntil = 0;
        seed();
      }, detonationMs);
    };
    detonateRef.current = detonate;

    resize();

    const resizeObserver = typeof ResizeObserver === "function" ? new ResizeObserver(resize) : null;
    resizeObserver?.observe(host);

    const intersectionObserver =
      typeof IntersectionObserver === "function"
        ? new IntersectionObserver(([entry]) => {
            visible = entry.isIntersecting;
            start();
          })
        : null;
    intersectionObserver?.observe(host);

    if (animate && interactive) {
      host.addEventListener("pointermove", onMove);
      host.addEventListener("pointerleave", onLeave);
      host.addEventListener("pointerdown", onDown);
    }
    if (animate) {
      start();
    }

    return () => {
      detonateRef.current = null;
      window.clearTimeout(reseedTimer);
      cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      intersectionObserver?.disconnect();
      host.removeEventListener("pointermove", onMove);
      host.removeEventListener("pointerleave", onLeave);
      host.removeEventListener("pointerdown", onDown);
    };
  }, [animate, tone, interactive]);

  useEffect(() => {
    if (explosion > 0) {
      detonateRef.current?.();
    }
  }, [explosion]);

  return <canvas ref={canvasRef} aria-hidden="true" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />;
}
